// 用户无需重新安装 app 即可获得新版本更新
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Preferences } from '@capacitor/preferences';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor, CapacitorHttp } from '@capacitor/core';

// 配置项（可抽离到单独文件）
const h5Url = 'https://tuanu.es/h5';
const VERSION_JSON = '/version.json';
const DIST_ZIP = '/app.zip';

// 类型定义（简化版，按需扩展）
interface BundleInfo {
  version?: string;
  id: string;
  [key: string]: any;
}

type NullableBundle = BundleInfo | null;

// 状态控制，防止并发
let pendingBundle: NullableBundle = null;
let isChecking = false;
let isDownloading = false;

// 持久化 key，用于恢复 pendingBundle
const PENDING_BUNDLE_KEY = 'updater:pendingBundle';

// 事件订阅句柄，用于清理
// const listeners: Array<{ remove: () => Promise<void> | void }> = [];

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function promiseTimeout<T>(p: Promise<T>, ms: number) {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('timeout')), ms);
    p.then((r) => {
      clearTimeout(id);
      resolve(r);
    }).catch((e) => {
      clearTimeout(id);
      reject(e);
    });
  });
}

async function downloadWithRetry(
  opts: { url: string; version: string },
  retries = 2,
  timeoutMs = 30_000,
) {
  let lastErr: any;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      isDownloading = true;
      const downloadPromise = CapacitorUpdater.download({
        url: opts.url,
        version: opts.version,
      });
      const res = await promiseTimeout(downloadPromise, timeoutMs);
      return res as BundleInfo;
    } catch (e) {
      lastErr = e;
      const backoff = Math.min(30_000, Math.pow(2, attempt) * 1000);
      console.warn(
        `download attempt ${attempt} failed, retrying in ${backoff}ms`,
        e,
      );
      await sleep(backoff);
      continue;
    } finally {
      isDownloading = false;
    }
  }
  throw lastErr;
}

function normalizeBundleArg(b: any): BundleInfo {
  if (!b) throw new Error('invalid bundle');
  if (typeof b === 'string') return { id: b } as BundleInfo;
  if (b.id) return b as BundleInfo;
  if (b.bundleId) return { id: b.bundleId, ...b } as BundleInfo;
  return b as BundleInfo;
}

async function checkVersion() {
  if (Capacitor.getPlatform() === 'web') return;
  if (isChecking) return;
  isChecking = true;
  try {
    console.log('进入更新检测============');
    const { value: oldVersion } = await Preferences.get({ key: 'version' });

    const res = await CapacitorHttp.get({ url: h5Url + VERSION_JSON });
    const newVersion = res?.data?.version;
    console.log('新版本信息============：', res.data);

    if (!newVersion) return;
    console.log('新版本-- %d，旧版本%d', newVersion, oldVersion);

    if (newVersion !== oldVersion) {
      // 避免重复下载
      if (isDownloading) {
        console.log('已有下载进行中，忽略本次触发');
        return;
      }
      try {
        const bundle = await downloadWithRetry(
          { url: h5Url + DIST_ZIP, version: newVersion },
          2,
          45_000,
        );
        pendingBundle = normalizeBundleArg(bundle);
        // 持久化 pendingBundle，保证在应用被杀或重启后仍可使用
        try {
          await persistPendingBundle(pendingBundle);
        } catch (e) {
          console.warn('persistPendingBundle failed', e);
        }
        // 保存新版本号，但只在下载成功后写入
        await Preferences.set({ key: 'version', value: newVersion });
        console.log('下载完成，已准备好更新：', pendingBundle);
      } catch (err) {
        console.error('下载失败：', err);
      }
    } else {
      console.log('版本一致，无需更新=====');
    }
  } catch (err) {
    console.error('检测版本出错：', err);
  } finally {
    isChecking = false;
  }
}

export function liveUpdate(aferAppMounted?: () => void) {
  return new Promise((resolve) => {
    // 启动时尝试恢复之前的 pendingBundle（如果存在）
    try {
      restorePendingBundle().catch((e) =>
        console.debug('restorePendingBundle error', e),
      );
    } catch (e) {
      // ignore
    }
    // 在应用准备好后通知 native
    try {
      CapacitorUpdater.notifyAppReady();
    } catch (e) {
      // ignore if plugin not available
    }

    // 首次检测（非阻塞）
    // checkVersion();

    const appStateHandle = App.addListener('appStateChange', async (state) => {
      console.log('appStateChange', state);
      if (state.isActive) {
        // 前台：尽量触发检测
        checkVersion();
        resolve(true);
      }

      // 进入后台时，如果已经准备好包，则安全激活
      if (!state.isActive && pendingBundle) {
        // SplashScreen.show();
        try {
          const arg = normalizeBundleArg(pendingBundle);
          // 优先使用 next() 安全安排更新（如果插件支持）
          if (typeof (CapacitorUpdater as any).next === 'function') {
            await (CapacitorUpdater as any).next(arg);
            console.log('调用 next 安排更新：', arg);
            // next() 已经安排更新，清理持久化的 pendingBundle
            try {
              await clearPendingBundle();
            } catch (e) {
              console.warn('clearPendingBundle after next failed', e);
            }
          } else {
            // fallback: 调用 set（注意：set 可能会销毁 JS 上下文）
            await CapacitorUpdater.set(arg);
            console.log('调用 set 应用更新：', arg);
            // set() 成功后清理持久化的 pendingBundle
            try {
              await clearPendingBundle();
            } catch (e) {
              console.warn('clearPendingBundle after set failed', e);
            }
          }
          pendingBundle = null;
        } catch (err) {
          console.error('激活更新失败：', err);
          // try {
          //   SplashScreen.hide();
          // } catch (e) {
          //   // ignore
          // }
        }
      }
      resolve(true);
    });
  });
}

// ========== pendingBundle 持久化操作 ============
async function persistPendingBundle(bundle: BundleInfo) {
  try {
    await Preferences.set({
      key: PENDING_BUNDLE_KEY,
      value: JSON.stringify(bundle),
    });
    console.log('persistPendingBundle saved');
  } catch (e) {
    console.warn('persistPendingBundle error', e);
    throw e;
  }
}

async function restorePendingBundle(): Promise<NullableBundle> {
  try {
    const { value } = await Preferences.get({ key: PENDING_BUNDLE_KEY });
    if (!value) return null;
    const parsed = JSON.parse(value) as BundleInfo;
    pendingBundle = normalizeBundleArg(parsed);
    console.log('restorePendingBundle restored', pendingBundle);
    return pendingBundle;
  } catch (e) {
    console.warn('restorePendingBundle error', e);
    return null;
  }
}

async function clearPendingBundle() {
  try {
    await Preferences.remove({ key: PENDING_BUNDLE_KEY });
    pendingBundle = null;
    console.log('clearPendingBundle removed');
  } catch (e) {
    console.warn('clearPendingBundle error', e);
    throw e;
  }
}
